/**
 * localStorage utility functions with prefix and snake_case keys
 * 
 * Keys are prefixed with the party ID to keep v1 and v2 (beta) data separate.
 * Example: mr_oscars_party_2026_user_id vs mr_oscars_party_2025_user_id
 */

/**
 * Convert camelCase to snake_case
 * @param {string} str - camelCase string
 * @returns {string} snake_case string
 */
const camelToSnake = (str) => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

/**
 * Get localStorage key with prefix based on party ID
 * @param {string} key - Key name (camelCase or snake_case)
 * @param {string} partyId - The active party ID for prefixing
 * @returns {string} Prefixed key
 */
const getStorageKey = (key, partyId) => {
  const prefix = `mr_${partyId}_`;
  // Convert camelCase to snake_case if needed
  const snakeKey = key.includes('_') ? key : camelToSnake(key);
  return `${prefix}${snakeKey}`;
};

/**
 * Get item from localStorage with prefix
 * @param {string} key - Key name
 * @param {string} partyId - The active party ID for prefixing
 * @returns {string | null} Stored value or null
 */
export const getStorageItem = (key, partyId) => {
  if (!partyId) {
    console.warn('getStorageItem called without partyId');
    return null;
  }
  try {
    return localStorage.getItem(getStorageKey(key, partyId));
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

/**
 * Set item in localStorage with prefix
 * @param {string} key - Key name
 * @param {string} value - Value to store
 * @param {string} partyId - The active party ID for prefixing
 */
export const setStorageItem = (key, value, partyId) => {
  if (!partyId) {
    console.warn('setStorageItem called without partyId');
    return;
  }
  try {
    localStorage.setItem(getStorageKey(key, partyId), value);
  } catch (error) {
    console.error('Error writing to localStorage:', error);
  }
};

/**
 * Remove item from localStorage with prefix
 * @param {string} key - Key name
 * @param {string} partyId - The active party ID for prefixing
 */
export const removeStorageItem = (key, partyId) => {
  if (!partyId) {
    console.warn('removeStorageItem called without partyId');
    return;
  }
  try {
    localStorage.removeItem(getStorageKey(key, partyId));
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

/**
 * Clear all items with the prefix for a specific party
 * @param {string} partyId - The active party ID for prefixing
 */
export const clearStorageItems = (partyId) => {
  if (!partyId) {
    console.warn('clearStorageItems called without partyId');
    return;
  }
  try {
    const prefix = `mr_${partyId}_`;
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};
